import { z } from 'zod';
import { PaymentMethodType } from '@/ar/network/AccountsReceivable.network';
import { encryptWithPem } from '@/utils/encryptUtils';

export type BankAccountType = PaymentMethodType.ACSS_DEBIT | PaymentMethodType.US_BANK_ACCOUNT;

export type EncryptedBankData = {
    type: BankAccountType,
    encryptedOwnerName: string,
    encryptedAccountNumber: string,
    encryptedBranchNumber?: string,
    encryptedInstitutionNumber?: string,
    encryptedRoutingNumber?: string,
};

export interface CreateBankAccountProps {
    handleClose: () => void,
    customerID: string,
    supplierID: string,
}

export type BankAccountDetails = {
    ownerName: string,
    branchNumber: string,
    institutionNumber: string,
    accountNumber: string,
    confirmAccountNumber: string,
    routingNumber?: string,
};

export const NUM_FIELDS: Record<BankAccountType, number> = {
    [PaymentMethodType.US_BANK_ACCOUNT]: 4,
    [PaymentMethodType.ACSS_DEBIT]: 5,
};

export const validationSchemaEFT = z.object({
    ownerName: z.string().nonempty('Name of account holder is required').min(2, 'Must be full name'),
    branchNumber: z.string()
        .nonempty('Branch number is required')
        .length(5, 'Must be 5 digits')
        .regex(/^\d+$/, 'Must contain only numbers'),
    institutionNumber: z.string()
        .nonempty('Institution number is required')
        .length(3, 'Must be 3 digits')
        .regex(/^\d+$/, 'Must contain only numbers'),
    accountNumber: z.string()
        .nonempty('Account number is required')
        .min(7, 'Must be 7-12 digits')
        .max(12, 'Must be 7-12 digits')
        .regex(/^\d+$/, 'Must contain only numbers'),
    confirmAccountNumber: z.string()
        .nonempty('Confirm account number is required')
        .min(7, 'Must be 7-12 digits')
        .max(12, 'Must be 7-12 digits')
        .regex(/^\d+$/, 'Must contain only numbers')
}).refine((data) => data.confirmAccountNumber === data.accountNumber, {
    message: 'Account numbers must match',
    path: ['confirmAccountNumber'],
});

const isValidRoutingNumber = (routingNumber: string): boolean => routingNumber.length === 9
    && routingNumber.split('').map(Number).reduce((sum, digit, index) => {
        const weights = [3, 7, 1];
        const weightIndex = index % 3;
        return sum + digit * weights[weightIndex];
    }, 0) % 10 === 0;

export const validationSchemaACH = z.object({
    ownerName: z.string().nonempty('Name of account holder is required').min(2, 'Must be full name'),
    routingNumber: z.string().nonempty('Routing number is required').length(9, 'Must be 9 digits'),
    accountNumber: z.string().nonempty('Account number is required').min(7, 'Must be 7-17 digits').max(17, 'Must be 7-17 digits'),
    confirmAccountNumber: z.string().nonempty('Confirm account number is required')
}).refine((data) => data.confirmAccountNumber === data.accountNumber, {
    message: 'Account numbers must match',
    path: ['confirmAccountNumber'],
}).refine((data) => isValidRoutingNumber(data.routingNumber), {
    message: 'Routing number is invalid. Please verify and re-enter',
    path: ['routingNumber'],
});

export const encryptBankAccountData = async (newFormBankAccount: BankAccountDetails, isACHPaymentMethod: boolean): Promise<EncryptedBankData> => {
    const { ownerName, accountNumber } = newFormBankAccount;

    const commonEncryptedData = {
        encryptedOwnerName: await encryptWithPem(ownerName),
        encryptedAccountNumber: await encryptWithPem(accountNumber),
    };

    if (isACHPaymentMethod) {
        if (!newFormBankAccount.routingNumber) {
            throw new Error('Routing number is required for ACH payments');
        }
        return {
            ...commonEncryptedData,
            type: PaymentMethodType.US_BANK_ACCOUNT,
            encryptedRoutingNumber: await encryptWithPem(newFormBankAccount.routingNumber),
        };
    }

    if (!newFormBankAccount.branchNumber || !newFormBankAccount.institutionNumber) {
        throw new Error('Branch number and institution number are required for EFT payments');
    }
    return {
        ...commonEncryptedData,
        type: isACHPaymentMethod ? PaymentMethodType.US_BANK_ACCOUNT : PaymentMethodType.ACSS_DEBIT,
        encryptedBranchNumber: await encryptWithPem(newFormBankAccount.branchNumber),
        encryptedInstitutionNumber: await encryptWithPem(newFormBankAccount.institutionNumber),
    };
};
